import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "./config";

/**
 * The adaptive intake, run from the site's hero against the real lesson server.
 * Types mirror mobile/src/reel/api.ts (which mirrors Session.state() in backend/src/session.ts);
 * only the fields the site reads are listed.
 */
export type Conf = "low" | "medium" | "high";
export type BriefField = { value: string; confidence: Conf };
export type Brief = {
  destination: BriefField;
  why: BriefField;
  startingPoint: BriefField;
  constraints: BriefField;
  success: BriefField;
  summary: string;
};
export type IntentAnswer = { chip?: string; text?: string; skipped?: boolean };
export type Intent = {
  status: "thinking" | "asking" | "brief" | "confirmed" | "error";
  error?: string;
  rev: number;
  asked: number;
  max: number;
  question?: { id: string; prompt: string; chips: { id: string; label: string }[] };
  brief?: Brief;
};
export type Live = {
  id: string;
  v: number;
  intake: { status: "pending" | "done" | "error"; error?: string; title?: string; trust?: "everyday" | "cited" | "grounded"; note?: string };
  intent?: Intent;
  research: { status: "off" | "running" | "done" | "error"; log?: string; sources: string[] };
  reel: { status: "idle" | "waiting" | "writing" | "done" | "error"; beats: unknown[]; error?: string };
};

class ApiError extends Error {
  constructor(
    message: string,
    readonly state?: Live,
  ) {
    super(message);
  }
}

const UNREACHABLE = "Couldn't reach the lesson server.";

async function call(path: string, init?: RequestInit): Promise<Live> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers: { "content-type": "application/json" } });
  } catch {
    throw new ApiError(UNREACHABLE);
  }
  const body = (await res.json().catch(() => null)) as (Live & { error?: string; state?: Live }) | null;
  if (!res.ok || !body) throw new ApiError(body?.error ?? "The lesson server had a problem.", body?.state);
  return body;
}

/** Poll for at most this long; a tab left open on a question shouldn't poll forever. */
const POLL_FOR_MS = 15 * 60 * 1000;

type Action = { kind: "start" } | { kind: "resume"; id: string } | { kind: "post"; path: string; body: unknown };

export function useIntake() {
  const [q, setQ] = useState<string | null>(null);
  const [live, setLive] = useState<Live | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState(0);
  const last = useRef<Action>({ kind: "start" });
  const liveRef = useRef<Live | null>(null);
  liveRef.current = live;

  // Never step back to an older state: a reply's response can be newer than a poll still in flight.
  const take = useCallback((s: Live) => {
    setLive(cur => (cur && cur.id === s.id && cur.v > s.v ? cur : s));
  }, []);

  // One run: start the session, then long-poll it (returns on change or after ~8 s), like useLiveReel.
  useEffect(() => {
    if (!q) return;
    let stop = false;
    const t0 = Date.now();
    const resume = last.current.kind === "resume" ? last.current.id : null;
    setError(null);
    if (!resume) setLive(null);
    let sid = resume;
    (async () => {
      try {
        // Resuming (the poll dropped): pick the same session back up instead of paying for a new intake.
        let s = resume ? await call(`/session/${resume}?v=-1`) : await call("/intake", { method: "POST", body: JSON.stringify({ q, adaptive: true }) });
        sid = s.id;
        if (stop) return;
        take(s);
        // Done once the brief is confirmed and the first lesson has started writing; the app takes it from there.
        const done = (x: Live) => x.intent?.status === "confirmed" && (x.reel.beats.length > 0 || x.reel.status === "done" || x.reel.status === "error");
        while (!stop && !done(s) && Date.now() - t0 < POLL_FOR_MS) {
          s = await call(`/session/${s.id}?v=${s.v}`);
          if (!stop) take(s);
        }
      } catch (e) {
        if (stop) return;
        if (sid) last.current = { kind: "resume", id: sid };
        setError((e as Error).message);
      }
    })();
    return () => {
      stop = true;
    };
  }, [q, run, take]);

  const post = useCallback(
    (path: string, body: unknown) => {
      const cur = liveRef.current;
      if (!cur) return;
      last.current = { kind: "post", path, body };
      setBusy(true);
      setError(null);
      call(`/session/${cur.id}${path}`, { method: "POST", body: JSON.stringify(body) })
        .then(take)
        .catch((e: ApiError) => {
          // 409: already answered / nothing waiting. The server sends its state; take it rather than erroring.
          if (e.state) take(e.state);
          else setError(e.message);
        })
        .finally(() => setBusy(false));
    },
    [take],
  );

  const start = useCallback((text: string) => {
    last.current = { kind: "start" };
    setQ(text);
    setRun(n => n + 1);
  }, []);

  const reset = useCallback(() => {
    setQ(null);
    setLive(null);
    setError(null);
  }, []);

  /** Retry what failed: the answer being sent, the dropped poll, or (if it never started) the intake. */
  const retry = useCallback(() => {
    const a = last.current;
    if (a.kind === "post" && liveRef.current) post(a.path, a.body);
    else setRun(n => n + 1);
  }, [post]);

  /** A fresh session for the same words (the intent planner itself failed). */
  const restart = useCallback(() => {
    last.current = { kind: "start" };
    setRun(n => n + 1);
  }, []);

  return {
    q,
    live,
    error,
    busy,
    start,
    reset,
    retry,
    restart,
    reply: (id: string, a: IntentAnswer) => post("/reply", { id, ...a }),
    confirm: () => post("/brief", { confirm: true }),
    edit: (text: string) => post("/brief", { edit: text }),
  };
}
